import React, { useState, useMemo, useCallback, useEffect } from 'react';
import styled from 'styled-components';

import MonitorService from 'services/monitor';

import useInterval from "hooks/useInterval";

import { Row, Col } from 'components/layout';
import Animate, { FadeIn } from 'components/Animate';
import { Text } from 'components/Text';
import Table from 'components/Table';

const TOP_EMPLOYEES_UPDATE_INTERVAL = 5 * 60 * 1000;

const TopEmplotyeesMonitor = ({ hardwareId }) => {
  const monitorService = useMemo(() => new MonitorService(), []);

  const [isLoading, setIsLoading] = useState(false);
  const [topEmployees, setTopEmployees] = useState({});

  const getTopEmployees = useCallback(() => {
    setIsLoading(true);
    try {
      const topEmployees = monitorService.getTopEmployees({ hw_id: hardwareId });
      if (!topEmployees) {
        return;
      }
      setTopEmployees(topEmployees);
    } finally {
      setIsLoading(false);
    }
  }, [monitorService, hardwareId]);

  useEffect(() => {
    getTopEmployees();
  }, [getTopEmployees]);

  useInterval(getTopEmployees, TOP_EMPLOYEES_UPDATE_INTERVAL);

  const topPerformers = useMemo(() => {
    if (topEmployees && topEmployees.length) {
      let counter = 1;
      const topPerformers = topEmployees.map(e => {
        e.nr = counter++;
        return e;
      });
      return [topPerformers.slice(0, 10), topPerformers.slice(10, 20)];
    }
    return [[], []];
  }, [topEmployees]);

  return (
    <Animate animation={FadeIn} duration="1s" timingFunction="linear" style={{ height: '100%' }}>
      <Row style={{ minHeight: '100%', backgroundColor: '#EFF2F5' }}>
        <Col span={24}>
          <Row justify="center" style={{ padding: '4.5vh 0 3.5vw 0' }}>
            <Col>
              <TopPerfomersTitle>Top 20 Performers</TopPerfomersTitle>
            </Col>
          </Row>
          <Row>
            <Col span={12} style={{ paddingLeft: '4vw', paddingRight: '1vw' }}>
              <PerformersTable
                className="left-table"
                bordered={false}
                dataSource={topPerformers[0]}
                pagination={false}
                loading={isLoading}
                noData={null}
                rowKey="nr"
              >
                <Table.Column type="text" dataIndex="nr" suffix="." />
                <Table.Column type="text" title="Associate ID" dataIndex="code" />
                <Table.Column type="text" title="Agency" align="center" dataIndex="agency_name" render={(agencyName, item) => {
                  if (item.logo_img) {
                    return <img src={item.logo_img} style={{ height: '2vw' }} alt={agencyName} />
                  } else {
                    return <Text>{agencyName}</Text>
                  }
                }} />
                <Table.Column type="number" title="Number of unloads" align="center" dataIndex="tot_unloads" />
                <Table.Column type="duration" title="Saved minutes" align="center" dataIndex="saved_mins_tot" inMins={true} withSecs={false} />
              </PerformersTable>
            </Col>
            <Col span={12} style={{ paddingLeft: '2vw', paddingRight: '4vw' }}>
              <PerformersTable
                className="right-table"
                bordered={false}
                dataSource={topPerformers[1]}
                pagination={false}
                loading={isLoading}
                noData={null}
                rowKey="nr"
              >
                <Table.Column type="text" dataIndex="nr" suffix="." />
                <Table.Column type="text" title="Associate ID" dataIndex="code" />
                <Table.Column type="text" title="Agency" align="center" dataIndex="agency_name" render={(agencyName, item) => {
                  if (item.logo_img) {
                    return <img src={item.logo_img} style={{ height: '2vw' }} alt={agencyName} />
                  } else {
                    return <Text>{agencyName}</Text>
                  }
                }} />
                <Table.Column type="number" title="Number of unloads" align="center" dataIndex="tot_unloads" />
                <Table.Column type="duration" title="Saved minutes" align="center" dataIndex="saved_mins_tot" inMins={true} withSecs={false} />
              </PerformersTable>
            </Col>
          </Row>
        </Col>
      </Row>
    </Animate>
  );
};

const TopPerfomersTitle = styled(Text)`
  font-family: Inter;
  font-style: normal;
  font-weight: 900;
  font-size: 3.75vw;
  line-height: 100%;
  color: #104663;
`;

const PerformersTable = styled(Table)`
  width: 100%;

  .ant-spin.ant-spin-spinning {
    .ant-spin-dot {
      width: 2vw;
      height: 2vw;
  
      .ant-spin-dot-item {
        width: 0.7vw;
        height: 0.7vw;
      }
    }
  }

  &.left-table {
    table > tbody > tr:nth-child(-n+3) td {
      background: #F5FDFF;
      box-shadow: inset 0px -1px 0px #DAE0E6;
      color: #2E8BB0;
      font-size: 1.55vw;

      span {
        color: #2E8BB0;
        font-size: 1.5vw;
      }
    }
  }

  .ant-table-content {
    table > tbody > tr td:nth-child(3) {
      white-space: nowrap !important;
    }

    table > thead > tr th {
      background-color: #E4E8ED;
      font-family: Inter;
      font-style: normal;
      font-weight: 600;
      font-size: 1.1vw;
      line-height: 120%;
      color: #232526;
      padding: 1vw 1.2vw !important;
    }

    table > tbody > tr td {
      font-family: Inter;
      font-style: normal;
      font-weight: 600;
      font-size: 1.2vw;
      line-height: 115%;
      padding: 0.8vw !important;
    }

    table > tbody tr td:first-child,
    table > tbody tr td:nth-child(2) {
      font-size: 1.4vw !important;
    }
  }
`;


export default TopEmplotyeesMonitor;